import React, { useState } from 'react';
import { Layout } from './Layout';
import { Button, Divider, Header, Message, Segment, Grid } from 'semantic-ui-react';
import { userManageClient } from '../userManageClient';
import { TwoFactorResponse } from '@app/api2/api';
import { useQuery } from '@tanstack/react-query';
import { buildUrl, AppRoutes } from '../../AppRoutes';
import { useTranslation2 } from '../../Contexts/TranslationContext2';
import { BPButton } from '@/components/bp/bpbutton';

export const TwoFactor: React.FC = () => {
    const { translations: t } = useTranslation2();
    const [actionError, setActionError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const { data: twoFactorInfo, isLoading: loading, error, refetch } = useQuery({
        queryKey: ['twoFactorInfo'],
        queryFn: async () => {
            const result = await userManageClient.getTwoFactorInfo();
            return result;
        },
        // onError: (error: any) => {
        //     console.error('Error loading two-factor info:', error);
        //     setActionError('Error loading two-factor authentication information.');
        // },
    });

    React.useEffect(() => {
        if (error) {
            console.error('Error loading two-factor info:', error);
            setActionError('Error loading two-factor authentication information.');
        }
    }, [error])

    const handleDisable2fa = async () => {
        setActionError('');
        setSuccessMessage('');

        try {
            const result = await userManageClient.disable2fa();

            if (result.success) {
                setSuccessMessage('2fa has been disabled. You can reenable 2fa when you setup an authenticator app.');
                await refetch(); // Reload to get updated data
            } else {
                setActionError(result.errors?.join(', ') || 'Failed to disable 2FA.');
            }
        } catch (error) {
            console.error('Disable 2FA error:', error);
            setActionError('An error occurred while disabling 2FA. Please try again.');
        }
    };

    const handleResetAuthenticator = async () => {
        if (!window.confirm('This will reset your authenticator app key and disable 2FA. Are you sure you want to continue?')) {
            return;
        }

        setActionError('');
        setSuccessMessage('');

        try {
            const result = await userManageClient.resetAuthenticator();

            if (result.success) {
                setSuccessMessage('Your authenticator app key has been reset. You will need to configure your authenticator app using the new key.');
                await refetch(); // Reload to get updated data
            } else {
                setActionError('Failed to reset authenticator key.');
            }
        } catch (error) {
            console.error('Reset authenticator error:', error);
            setActionError('An error occurred while resetting the authenticator key. Please try again.');
        }
    };

    if (loading) {
        return (
            <Layout>
                <Header as="h1">Two-factor authentication (2FA)</Header>
                <Segment loading>
                    <div style={{ height: '200px' }} />
                </Segment>
            </Layout>
        );
    }

    if (error || !twoFactorInfo) {
        return (
            <Layout>
                <Header as="h1">Two-factor authentication (2FA)</Header>
                <Message error>
                    Unable to load two-factor authentication information.
                </Message>
            </Layout>
        );
    }

    async function handleForgetBrowser(event: React.MouseEvent<HTMLButtonElement>, data: any): Promise<void> {
        if (!window.confirm('This will reset your authenticator app key and disable 2FA. Are you sure you want to continue?')) {
            return;
        }

        setActionError('');
        setSuccessMessage('');

        try {
            const result = await userManageClient.forgetBrowser();

            if (result) {
                setSuccessMessage('Your authenticator app key has been reset. You will need to configure your authenticator app using the new key.');
                await refetch(); // Reload to get updated data
            } else {
                setActionError('Failed to reset authenticator key.');
            }
        } catch (error) {
            console.error('Reset authenticator error:', error);
            setActionError('An error occurred while resetting the authenticator key. Please try again.');
        }
    }

    return (
        <Layout>
            {/* <Header as="h2">Two-factor authentication (2FA)</Header> */}

            {successMessage && (
                <Message success>
                    {successMessage}
                </Message>
            )}

            {actionError && (
                <Message error>
                    {actionError}
                </Message>
            )}

            <Grid stackable>
                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3">Authenticator app</Header>
                        <p>
                            {twoFactorInfo.hasAuthenticator
                                ? ''
                                : 'You have not set up an authenticator app.'}
                        </p>

                        <div style={{ marginBottom: '1rem' }}>
                            <BPButton
                                as="a"
                                href={buildUrl(AppRoutes.ManageEnableAuthenticator, {})}
                                primary={!twoFactorInfo.hasAuthenticator}
                                basic={twoFactorInfo.hasAuthenticator}
                            >
                                {'Set up authenticator app'}
                            </BPButton>
                        </div>

                        {twoFactorInfo.hasAuthenticator && (
                            <div>
                                <BPButton
                                    onClick={handleResetAuthenticator}
                                    negative
                                >
                                    Reset authenticator app
                                </BPButton>
                            </div>
                        )}
                    </Segment>
                </Grid.Column>

                <Grid.Column width={8}>
                    <Segment>
                        <Header as="h3">Recovery codes</Header>
                        <p>
                            You have <strong>{twoFactorInfo.recoveryCodesLeft} recovery codes</strong> remaining.
                        </p>

                        <div style={{ marginBottom: '1rem' }}>
                            <BPButton
                                as="a"
                                href={buildUrl(AppRoutes.ManageGenerateRecoveryCodes, {})}
                                basic
                            >
                                Generate recovery codes
                            </BPButton>
                        </div>
                    </Segment>
                </Grid.Column>
            </Grid>

            {twoFactorInfo.is2faEnabled && (
                <Segment>
                    <Header as="h3">Disable 2FA</Header>
                    <p>
                        <strong>Disabling 2FA does not change the keys used in authenticator apps.</strong>
                        If you wish to change the key used in an authenticator app you should
                        reset your authenticator keys.
                    </p>

                    <BPButton
                        onClick={handleDisable2fa}
                        negative
                    >
                        Disable 2FA
                    </BPButton>
                </Segment>
            )}

            {twoFactorInfo.isMachineRemembered && (
                <Segment>
                    <Header as="h3">This browser</Header>
                    <p>
                        This browser is remembered. You will not be asked for your 2FA code when logging in from this browser.
                    </p>
                    <BPButton onClick={handleForgetBrowser} negative>Forget this browser</BPButton>
                </Segment>
            )}
        </Layout>
    );
};